import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { getStatusStyle } from '../statusStyles';

const WeekDetailModal = ({
  isOpen,
  onClose,
  selectedWeek,
  weeks,
  selectedSwapSurgeon1,
  setSelectedSwapSurgeon1,
  selectedSwapSurgeon2,
  setSelectedSwapSurgeon2,
  getConsultantNames,
  addSwap,
  baseStaff,
  onAddAdditionalActivity,
  onRemoveAdditionalActivity,
  onAddAdditionalPerson,
  onRemoveAdditionalPerson
}) => {
  const [selectedAdditionalPerson, setSelectedAdditionalPerson] = useState({});
  const [selectedAdditionalActivity, setSelectedAdditionalActivity] = useState({});

  if (!isOpen || !selectedWeek) return null;

  // Use fresh week data from weeks array if available, otherwise use selectedWeek
  const currentWeek = weeks && selectedWeek
    ? weeks.find(w => w.weekNumber === selectedWeek.weekNumber) || selectedWeek
    : selectedWeek;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold">
                Week {currentWeek.weekNumber + 1} Details - Starting {currentWeek.startDate.toLocaleDateString()}
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Days Grid */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map((dayName, dayIndex) => {
                  const day = currentWeek.days[dayIndex];
                  const style = getStatusStyle(day.status);

                  return (
                    <div key={dayIndex} className={`rounded-lg border ${style.border} ${style.soft} overflow-hidden`}>
                      {/* Status header — day, date and the staffing figure share one coloured box */}
                      <div className={`px-3 py-2 ${style.table}`}>
                        <div className="flex items-baseline justify-between gap-2">
                          <h3 className="font-semibold leading-tight">{dayName}</h3>
                          <span className="text-xs font-medium opacity-70">{day.date.toLocaleDateString()}</span>
                        </div>
                        <div className="text-2xl font-bold leading-tight mt-0.5">
                          {day.available} / {day.required}
                        </div>
                      </div>

                      <div className="p-3 space-y-3 text-xs">
                        {/* On call — the headline person for the day */}
                        {day.onCall && (
                          <div className="rounded-md bg-slate-800 text-white px-2.5 py-1.5">
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-300">On Call</div>
                            {day.onCallOff ? (
                              day.onCallCover ? (
                                <div className="text-sm font-bold">{day.onCallCover} <span className="text-[10px] font-semibold text-amber-100 bg-amber-700 rounded px-1 align-middle">additional</span></div>
                              ) : (
                                <div className="text-sm font-bold"><span className="line-through text-slate-400">{day.onCall}</span> <span className="text-[10px] font-semibold text-red-50 bg-red-600 rounded px-1 align-middle">cover needed</span></div>
                              )
                            ) : (
                              <div className="text-sm font-bold">{day.onCall}</div>
                            )}
                          </div>
                        )}

                        {day.swap && (
                          <div className="text-slate-600">
                            <span className="font-semibold">Swap:</span> {day.swap.originalSurgeon} → {day.swap.newSurgeon}
                          </div>
                        )}

                        {/* Roster — who is here, allocated, or away.
                            Left edge: slate = allocated, green = available, red = on leave. */}
                        {day.assigned.length > 0 && (
                          <div>
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Assigned</div>
                            <div className="space-y-1">
                              {day.assigned.map((a, i) => (
                                <div
                                  key={i}
                                  className={`rounded border border-slate-200 border-l-4 bg-white px-2 py-1 ${a.available ? 'border-l-slate-400' : 'border-l-red-500'}`}
                                >
                                  <div className={`font-semibold ${a.available ? 'text-slate-900' : 'text-red-700'}`}>
                                    {a.name}{!a.available && <span className="font-normal"> (on leave)</span>}
                                  </div>
                                  <div className={a.available ? 'text-slate-500' : 'text-red-600'}>{a.duty}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {day.availableFlexible.length > 0 && (
                          <div>
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Available</div>
                            <div className="space-y-1">
                              {day.availableFlexible.map((name, i) => (
                                <div key={i} className="rounded border border-slate-200 border-l-4 border-l-emerald-400 bg-white px-2 py-1 text-slate-800">
                                  {name}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {day.onLeave.length > 0 && (
                          <div>
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-red-600 mb-1">On Leave</div>
                            <div className="space-y-1">
                              {day.onLeave.map((l, i) => (
                                <div key={i} className="rounded border border-red-200 border-l-4 border-l-red-500 bg-white px-2 py-1 text-red-700">
                                  {l.name} <span className="text-red-500">({l.reason})</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Additional — manual extras, set apart from the roster */}
                        <div className="rounded-md bg-slate-100 border border-slate-200 p-2 space-y-2">
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Additional</div>

                          {/* Additional Activities */}
                          <div>
                            <div className="font-semibold text-slate-600 mb-1">Activities</div>
                            <div className="space-y-1 mb-1">
                              {day.additionalActivities && day.additionalActivities.length > 0 ? (
                                day.additionalActivities.map((activityName, i) => (
                                  <div key={i} className="rounded bg-white border border-slate-200 px-2 py-1 flex items-center justify-between">
                                    <span>{activityName}</span>
                                    <button
                                      onClick={() => onRemoveAdditionalActivity && onRemoveAdditionalActivity(day.date, activityName)}
                                      className="ml-1 p-0.5 text-slate-400 hover:text-red-600"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))
                              ) : (
                                <div className="text-slate-400 italic">None</div>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <input
                                type="text"
                                value={selectedAdditionalActivity[dayIndex] || ''}
                                onChange={(e) => setSelectedAdditionalActivity({
                                  ...selectedAdditionalActivity,
                                  [dayIndex]: e.target.value
                                })}
                                placeholder="e.g., Extra Theatre"
                                className="border border-slate-300 rounded-md px-2 py-1.5 flex-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                              <button
                                onClick={() => {
                                  if (selectedAdditionalActivity[dayIndex] && onAddAdditionalActivity) {
                                    onAddAdditionalActivity(day.date, selectedAdditionalActivity[dayIndex]);
                                    setSelectedAdditionalActivity({
                                      ...selectedAdditionalActivity,
                                      [dayIndex]: ''
                                    });
                                  }
                                }}
                                className="px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                            {day.additionalActivities && day.additionalActivities.length > 0 && (
                              <div className="text-slate-500 mt-1">
                                Requires {day.requiredActivities} people (+{day.additionalActivities.length} extra)
                              </div>
                            )}
                          </div>

                          {/* Additional People */}
                          <div>
                            <div className="font-semibold text-slate-600 mb-1">People</div>
                            <div className="space-y-1 mb-1">
                              {day.additionalPeople && day.additionalPeople.length > 0 ? (
                                day.additionalPeople.map((name, i) => {
                                  const isAvailable = day.availableAdditionalPeople && day.availableAdditionalPeople.includes(name);
                                  return (
                                    <div key={i} className={`rounded bg-white border px-2 py-1 flex items-center justify-between ${
                                      isAvailable ? 'border-slate-200' : 'border-red-200 border-l-4 border-l-red-500 text-red-700'
                                    }`}>
                                      <span>{name} {!isAvailable && '(on leave)'}</span>
                                      <button
                                        onClick={() => onRemoveAdditionalPerson && onRemoveAdditionalPerson(day.date, name)}
                                        className="ml-1 p-0.5 text-slate-400 hover:text-red-600"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="text-slate-400 italic">None</div>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <select
                                value={selectedAdditionalPerson[dayIndex] || ''}
                                onChange={(e) => setSelectedAdditionalPerson({
                                  ...selectedAdditionalPerson,
                                  [dayIndex]: e.target.value
                                })}
                                className="border border-slate-300 rounded-md px-2 py-1.5 flex-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              >
                                <option value="">Select person...</option>
                                {getConsultantNames && getConsultantNames().map((name, i) => (
                                  <option key={i} value={name}>{name}</option>
                                ))}
                              </select>
                              <button
                                onClick={() => {
                                  if (selectedAdditionalPerson[dayIndex] && onAddAdditionalPerson) {
                                    onAddAdditionalPerson(day.date, selectedAdditionalPerson[dayIndex]);
                                    setSelectedAdditionalPerson({
                                      ...selectedAdditionalPerson,
                                      [dayIndex]: ''
                                    });
                                  }
                                }}
                                className="px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WeekDetailModal;